import React, { useState, useCallback, useMemo } from 'react';
import { Container, Card, Button, Modal } from 'react-bootstrap';
import { FiEye, FiUser } from 'react-icons/fi';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import { useSearch, SearchFetchFn, SearchPage } from '@core/hooks/useSearch';
import { fetchEntityUsers } from '@features/user/api/userApi';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import { EntityUser } from '@features/user/types/user';
import UserDetailsContactFields from '@features/user/components/UserDetailsContactFields';
import ApiResponse from '@core/types/apiResponse';
import PaginationBar from '@core/components/PaginationBar';
import TablePaginationFooter, { getPaginationRange } from '@core/components/TablePaginationFooter';
import {
  SORT_BY_EMAIL,
  SORT_BY_FNAME,
  SORT_BY_LNAME,
  SORT_BY_TRN,
  SORT_DIR_ASC,
  SORT_DIR_DESC,
  filtersWithoutSort,
  normalizeSortDir,
} from '@core/constants/listSort';
import {
  formatRolesByDepartmentLines,
} from '@features/user/utils/formatRolesByDepartment';

const PEOPLE_SORT_OPTIONS = [
  { value: `${SORT_BY_LNAME}:${SORT_DIR_ASC}`, label: 'Last name A–Z' },
  { value: `${SORT_BY_LNAME}:${SORT_DIR_DESC}`, label: 'Last name Z–A' },
  { value: `${SORT_BY_FNAME}:${SORT_DIR_ASC}`, label: 'First name A–Z' },
  { value: `${SORT_BY_FNAME}:${SORT_DIR_DESC}`, label: 'First name Z–A' },
  { value: `${SORT_BY_EMAIL}:${SORT_DIR_ASC}`, label: 'Email A–Z' },
  { value: `${SORT_BY_EMAIL}:${SORT_DIR_DESC}`, label: 'Email Z–A' },
  { value: `${SORT_BY_TRN}:${SORT_DIR_ASC}`, label: 'TRN A–Z' },
  { value: `${SORT_BY_TRN}:${SORT_DIR_DESC}`, label: 'TRN Z–A' },
];

const USER_FILTER_FIELDS: FilterField[] = [
  { name: 'firstName', label: 'First Name', type: 'text', placeholder: 'Filter by first name', colWidth: 4 },
  { name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Filter by last name', colWidth: 4 },
  { name: 'email', label: 'Email', type: 'text', placeholder: 'Filter by email', colWidth: 4 },
  { name: 'trn', label: 'TRN', type: 'text', placeholder: 'Filter by TRN', colWidth: 4 },
  {
    name: 'peopleSort',
    label: 'Sort',
    type: 'select',
    includeEmptyOption: false,
    colWidth: 4,
    options: PEOPLE_SORT_OPTIONS,
  },
];

export interface EntityUsersPageProps {
  scope: 'NATION' | 'DISTRICT' | 'CHURCH';
  scopeRefId: number;
  title: string;
  subtitle: string;
  /** Optional control rendered inline before the search input (e.g. entity scope picker). */
  leadingControl?: React.ReactNode;
  testId?: string;
}

function parsePeopleSort(filters: Record<string, any>): { sortBy: string; sortDir: string } {
  const raw = String(filters.peopleSort || `${SORT_BY_LNAME}:${SORT_DIR_ASC}`);
  const [sortBy, sortDir] = raw.split(':');
  const allowed = new Set([SORT_BY_FNAME, SORT_BY_LNAME, SORT_BY_TRN, SORT_BY_EMAIL]);
  return {
    sortBy: allowed.has(sortBy) ? sortBy : SORT_BY_LNAME,
    sortDir: normalizeSortDir(sortDir),
  };
}

const EntityUsersPage: React.FC<EntityUsersPageProps> = ({
  scope,
  scopeRefId,
  title,
  subtitle,
  leadingControl,
  testId = 'entity-users-search',
}) => {
  const [selectedUser, setSelectedUser] = useState<EntityUser | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const fetchFn = useCallback<SearchFetchFn<EntityUser>>(
    ({ freetext, page, perPage, filters }) => {
      const { sortBy, sortDir } = parsePeopleSort(filters);
      const contentFilters = filtersWithoutSort({ ...filters });
      delete contentFilters.peopleSort;
      const filterEntries = Object.entries(contentFilters).filter(
        ([, v]) => v !== undefined && v !== null && v !== ''
      );
      const hasSearch = Boolean(freetext?.trim()) || filterEntries.length > 0;
      return fetchEntityUsers(scope, scopeRefId, {
        ...(freetext?.trim() ? { freetext: freetext.trim() } : {}),
        ...Object.fromEntries(filterEntries),
        ...(hasSearch ? {} : { all: 'true' }),
        sortBy,
        sortDir,
        page: String(page),
        perPage: String(perPage),
      }) as unknown as Promise<ApiResponse<SearchPage<EntityUser>>>;
    },
    [scope, scopeRefId]
  );

  const search = useSearch<EntityUser>({
    fetchFn,
    initialPerPage: 20,
    enabled: !!scopeRefId,
    syncUrl: true,
  });

  const activeFilterCount = Object.entries(search.filters).filter(
    ([key, value]) =>
      key !== 'peopleSort' &&
      value !== undefined &&
      value !== null &&
      value !== ''
  ).length;

  const { start, end } = getPaginationRange({
    from: search.from,
    page: search.page,
    perPage: search.perPage,
    total: search.total,
    currentCount: search.data.length,
  });

  const handleView = (user: EntityUser) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const workplaceLabel = useMemo(() => {
    const first = selectedUser?.profiles?.[0];
    if (!first) return null;
    return `${first.scope_ref_name} (${first.scope_type})`;
  }, [selectedUser]);

  const columns: DataTableColumn<EntityUser>[] = [
    {
      key: 'name',
      label: 'Name',
      role: 'title',
      cardRender: (_value: any, row: EntityUser) => `${row.fname} ${row.lname}`,
      render: (_value: any, row: EntityUser) => (
        <div className="fw-medium">{row.fname} {row.lname}</div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      cardLabel: '',
      cardRender: (_value: any, row: EntityUser) => row.email || null,
      render: (_value: any, row: EntityUser) => (
        <span className="text-muted">{row.email}</span>
      ),
    },
    {
      key: 'trn',
      label: 'TRN',
      cardRender: (_value: any, row: EntityUser) => row.trn || null,
      render: (_value: any, row: EntityUser) => (
        <span className="text-muted">{row.trn || '—'}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-end',
      role: 'actions',
      render: (_value: any, row: EntityUser) => (
        <TableActions aria-label={`Actions for ${row.fname} ${row.lname}`}>
          <TableActionButton
            icon={<FiEye />}
            label="View"
            primary
            variant="outline-primary"
            onClick={() => handleView(row)}
          />
        </TableActions>
      ),
    },
  ];

  return (
    <Container fluid className="py-4">
      <div className="ntcog-page-title-block mb-4">
        <h1 className="ntcog-page-heading mb-1">{title}</h1>
        <p className="text-muted mb-0">{subtitle}</p>
      </div>

      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search by name, email, or TRN…"
          filterFields={USER_FILTER_FIELDS}
          initialQuery={search.initialQuery}
          onQueryChange={search.setQuery}
          onFiltersApply={search.setFilters}
          onClear={search.reset}
          loading={search.loading}
          activeFilterCount={activeFilterCount}
          filtersIgnored={search.filtersIgnored}
          leadingControl={leadingControl}
          testId={testId}
        />
      </div>

      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={search.data}
            columns={columns}
            rowNumberOffset={(search.from ?? 1) - 1}
            loading={search.loading}
            error={search.error}
            emptyMessage="No users found"
            cardAriaLabel={(row) => `User ${row.fname} ${row.lname}`}
            striped
            hover
            responsive
          />
        </Card.Body>
      </Card>

      {search.lastPage > 1 && (
        <TablePaginationFooter
          className="mt-3"
          info={`Showing ${start} to ${end} of ${search.total} users`}
        >
          <PaginationBar
            page={search.page}
            totalPages={search.lastPage}
            onPage={search.setPage}
          />
        </TablePaginationFooter>
      )}

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        scrollable
        dialogClassName="modal-dialog-scrollable"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold">
            <FiUser className="me-2" aria-hidden />
            User Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <div className="mb-3">
                <div className="text-muted small">Name</div>
                <div className="fw-medium">
                  {selectedUser.fname} {selectedUser.lname}
                </div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">Email</div>
                <div>{selectedUser.email}</div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">TRN</div>
                <div>{selectedUser.trn || 'N/A'}</div>
              </div>
              <UserDetailsContactFields
                mobile={selectedUser.phone_mobile_no}
                home={selectedUser.phone_home_no}
                office={selectedUser.phone_office_no}
                mailingAddress={selectedUser.mailing_address}
                ministerId={selectedUser.minister_id}
              />
              {workplaceLabel && (
                <div className="mb-3">
                  <div className="text-muted small">Scope</div>
                  <div>{workplaceLabel}</div>
                </div>
              )}
              <div className="mb-3">
                <div className="text-muted small">Roles</div>
                {formatRolesByDepartmentLines(selectedUser.profiles).length === 0 ? (
                  <div>No roles assigned</div>
                ) : (
                  formatRolesByDepartmentLines(selectedUser.profiles).map((group) => (
                    <div key={group.department} className="mb-1">
                      <span className="fw-medium">{group.department}:</span> {group.roles}
                    </div>
                  ))
                )}
              </div>
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="primary"
                  size="lg"
                  className="btn-action"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EntityUsersPage;
